// Browser-side purchase signal for the Microsoft Advertising UET tag in GTM.
//
// Purchases are otherwise recorded server-side only (payment-analytics.ts), so
// no browser tag ever sees one. This pushes a dedicated dataLayer event the
// moment the buyer's screen confirms a completed payment. It deliberately
// bypasses trackEvent: the name `purchase` would collide with the GA4
// purchase the server already sends, and Umami and PostHog already get the
// server-side event. Only the GTM trigger for this exact name should react.

const debugEnabled = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";
const shouldTrack = process.env.NODE_ENV === "production" || debugEnabled;

export const MICROSOFT_ADS_PURCHASE_EVENT = "ms_ads_purchase";

const TRACKED_STORAGE_PREFIX = "hyred.ms_ads_purchase.";

export interface MicrosoftAdsPurchase {
  paymentId: string;
  amount: number | null | undefined;
  tier?: string | null;
}

export interface PaymentStatusSnapshot {
  status?: string;
  amount?: number | null;
  tier?: string | null;
}

/**
 * Pushes the purchase at most once per payment in this browser. The success
 * screen can render again (polling, reload, the redirect page after a hosted
 * checkout), and UET would count each push as another conversion. Zero-value
 * completions (free vouchers) are never sent, matching the server-side rule
 * that only real revenue reaches ad platforms.
 *
 * Returns whether an event was pushed.
 */
export function trackMicrosoftAdsPurchase({
  paymentId,
  amount,
  tier,
}: MicrosoftAdsPurchase): boolean {
  if (typeof window === "undefined" || !shouldTrack) {
    return false;
  }

  if (!paymentId || typeof amount !== "number" || !(amount > 0)) {
    return false;
  }

  const storageKey = `${TRACKED_STORAGE_PREFIX}${paymentId}`;

  if (readStorage(storageKey)) {
    return false;
  }

  writeStorage(storageKey);

  const eventData = {
    event: MICROSOFT_ADS_PURCHASE_EVENT,
    event_id: `${MICROSOFT_ADS_PURCHASE_EVENT}_${paymentId}`,
    payment_id: paymentId,
    value: amount,
    currency: "IDR",
    tier: tier ?? "unknown",
  };

  if (debugEnabled) {
    console.log("[analytics]", MICROSOFT_ADS_PURCHASE_EVENT, eventData);
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);

  return true;
}

function readStorage(key: string) {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function writeStorage(key: string) {
  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    // Storage blocked: the in-page latch in the caller still prevents repeats
    // within this visit.
  }
}
